import type { ReactElement } from 'react';

/**
 * Stroke-based line icon set for the media-pair product_divider /
 * product_plan `dual_path` layout (`docs/momentum-v3-slides.md`
 * product_divider / product_plan; media-pair-contract.md). Every icon is a
 * 24-unit viewBox, `currentColor` stroke, no fill — callers set size/colour
 * via CSS on the wrapping element exactly like every other v3 primitive
 * (`--mv3-mint`, `--mv3-light-text`, etc). None of these render text or a
 * third-party brand mark.
 */

export interface IconProps {
  className?: string;
  size?: number;
}

function IconBase({
  className,
  size = 24,
  children,
}: IconProps & { children: ReactElement | ReactElement[] }): ReactElement {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Ticket path icon (default path 0). */
export function TicketIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1.5a1.5 1.5 0 0 0 0 3V15a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1.5a1.5 1.5 0 0 0 0-3V9Z" />
      <path d="M9 7v10" strokeDasharray="2 2" />
    </IconBase>
  );
}

/** Briefcase icon (default path 1 / corporate). */
export function BriefcaseIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <rect x="3" y="8" width="18" height="11" rx="2" />
      <path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M3 13h18" />
    </IconBase>
  );
}

export function CalendarIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </IconBase>
  );
}

export function CartIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
      <path d="M2.5 3h2.4l2.2 12.1a2 2 0 0 0 2 1.65h8.1a2 2 0 0 0 1.97-1.63L21 8H6.2" />
    </IconBase>
  );
}

export function PhoneIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M6.6 3.5h2.2l1.2 4-1.9 1.4a11.4 11.4 0 0 0 5 5l1.4-1.9 4 1.2v2.2a2 2 0 0 1-2.2 2A16.4 16.4 0 0 1 4.6 5.7a2 2 0 0 1 2-2.2Z" />
    </IconBase>
  );
}

export function StoreIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M3 9.5 4.5 4h15L21 9.5" />
      <path d="M4 9.5V19a1 1 0 0 0 1 1h4v-6h6v6h4a1 1 0 0 0 1-1V9.5" />
      <path d="M3 9.5a2.2 2.2 0 0 0 4.4.2 2.2 2.2 0 0 0 4.4 0 2.2 2.2 0 0 0 4.4 0 2.2 2.2 0 0 0 4.4-.2" />
    </IconBase>
  );
}

/** Landing-experience icon, path index 0 (public / calendar-driven landing). */
export function CalendarMonitorIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <rect x="2.5" y="4" width="19" height="13" rx="1.6" />
      <path d="M9 20.5h6M12 17v3.5" />
      <rect x="6.5" y="7.2" width="11" height="7" rx="1" />
      <path d="M9 6v2.2M15 6v2.2" />
    </IconBase>
  );
}

/** Landing-experience icon, path index 1 (form / inquiry-driven landing). */
export function FormCursorIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <rect x="4" y="3.5" width="12" height="16" rx="1.6" />
      <path d="M7 8h6M7 11.5h6M7 15h3" />
      <path d="M14.5 14.5 20 17l-2.4.7-.7 2.4-2.4-5.6Z" />
    </IconBase>
  );
}

export function PeopleIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M15.2 12.1a4.5 4.5 0 0 1 5.3 4.4" />
    </IconBase>
  );
}

export function ShieldIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M12 3.2 19 6v5.4c0 4.6-3 7.9-7 9.4-4-1.5-7-4.8-7-9.4V6l7-2.8Z" />
      <path d="M9 12l2 2 4-4" />
    </IconBase>
  );
}

export function MagnifierIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M20 20l-4.8-4.8" />
    </IconBase>
  );
}

export function MicIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5v3.3M9 20.8h6" />
    </IconBase>
  );
}

export function CloseIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </IconBase>
  );
}

export function ChevronRightIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M9 5l7 7-7 7" />
    </IconBase>
  );
}

/** TV icon (`ChannelHero` ctv chip / details-slide "WHERE IT RUNS" card, media-pairs-all-contract.md). */
export function TvIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <rect x="2.5" y="6" width="19" height="13" rx="2" />
      <path d="M8 3.5 12 6l4-2.5M9 22h6" />
    </IconBase>
  );
}

/** Play icon (online_video "WHERE IT RUNS" card / hero play bar). */
export function PlayIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M10 8.3v7.4l6-3.7-6-3.7Z" />
    </IconBase>
  );
}

/** Headphones icon (streaming_audio "WHERE IT RUNS" card / hero transport row). */
export function HeadphonesIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M4 13.5v-2a8 8 0 0 1 16 0v2" />
      <rect x="3" y="13" width="4.5" height="7" rx="1.6" />
      <rect x="16.5" y="13" width="4.5" height="7" rx="1.6" />
    </IconBase>
  );
}

/** Megaphone icon ("THE MESSAGE" card / audience_plan prospecting lane default). */
export function MegaphoneIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M3 10v4a1.5 1.5 0 0 0 1.5 1.5H6l4.2 3.4a1 1 0 0 0 1.6-.8V6.4a1 1 0 0 0-1.6-.8L6 9H4.5A1.5 1.5 0 0 0 3 10.5Z" />
      <path d="M15.5 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" />
    </IconBase>
  );
}

/** Refresh/retarget-arrows icon (audience_plan retargeting lane default / callout). */
export function RefreshIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M4 12a8 8 0 0 1 13.7-5.6L20 8.5M20 12a8 8 0 0 1-13.7 5.6L4 15.5" />
      <path d="M20 4.5v4h-4M4 19.5v-4h4" />
    </IconBase>
  );
}

/** Mail icon (email hero inbox row / "WHERE IT RUNS" card). */
export function MailIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M3.2 6.3 12 13l8.8-6.7" />
    </IconBase>
  );
}

/** Map-pin icon (mobile_location hero map / "WHO SEES IT" geo line). */
export function MapPinIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M12 21.5c4-4 7-7.9 7-11.5a7 7 0 0 0-14 0c0 3.6 3 7.5 7 11.5Z" />
      <circle cx="12" cy="10" r="2.6" />
    </IconBase>
  );
}

/** Trend-up icon (awareness_plan payoff callout). */
export function TrendUpIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <path d="M3 17l6-6.5 4 4L21 6" />
      <path d="M15 6h6v6" />
    </IconBase>
  );
}

/** Info icon (audience_plan note callout). */
export function InfoIcon(props: IconProps): ReactElement {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 11v6M12 7.2v.1" />
    </IconBase>
  );
}

export type PlanPathIconKind = 'ticket' | 'briefcase' | 'calendar' | 'cart' | 'phone' | 'store';

const PLAN_PATH_ICONS: Record<PlanPathIconKind, (props: IconProps) => ReactElement> = {
  ticket: TicketIcon,
  briefcase: BriefcaseIcon,
  calendar: CalendarIcon,
  cart: CartIcon,
  phone: PhoneIcon,
  store: StoreIcon,
};

/** `PlanPath.icon`, defaulting by card index (0 → ticket, 1 → briefcase) when absent. */
export function planPathIcon(icon: PlanPathIconKind | undefined, index: number, props?: IconProps): ReactElement {
  const kind = icon ?? (index === 0 ? 'ticket' : 'briefcase');
  const Icon = PLAN_PATH_ICONS[kind];
  return <Icon {...props} />;
}

/** Landing-experience icon by card index (0 → calendar-on-monitor, 1 → form-with-cursor). */
export function landingExperienceIcon(index: number, props?: IconProps): ReactElement {
  return index === 0 ? <CalendarMonitorIcon {...props} /> : <FormCursorIcon {...props} />;
}
